import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { PageHeader } from './PageHeader';
import './RoadMapPage.css';

interface WhiteListStats {
  total_spots: number;
  spots_taken: number;
  remaining_spots: number;
  white_list_start_date: string;
  opendoor_start_date?: string | null;
}

interface WhiteListStatus {
  in_white_list: boolean;
  nft_id?: number;
  joined_at?: string;
}

const GRANITE_TARGET_PRICE = 500_000;

interface RoadMapPageProps {
  stats: WhiteListStats | null;
  whiteListStatus: WhiteListStatus | null;
  maxUserStepPrice?: number;
  showBack?: boolean;
}

type StageStatus = 'pending' | 'active' | 'completed';

const GRANITE_FEATURES = [
  { icon: '/upgrade-s.png', title: 'Апгрейд NFT до максимального тира', desc: '(возможность самому взять финальный приз)' },
  { icon: '/shield-s.png', title: 'Гранитный статус', desc: '(вывод NFT в блокчейн)' },
  { icon: '/medal.png', title: 'Повышенные призы', desc: '(оффлайн встреча со звездой)' },
  { icon: '/timer.png', title: 'Финальная торговая фаза', desc: '(спасение ху#ков от вымирания)' },
  { icon: '/pin.png', title: 'Оффлайн мероприятие', desc: '(пропуск в закрытый клуб)' },
];

const OPEN_DOOR_FEATURES = [
  { icon: '/buy.png', text: 'Свободная покупка NFT' },
  { icon: '/benefit-s.png', text: 'Участие в дропах призов' },
  { icon: '/sales.png', text: 'Автоматическая торговля' },
  { icon: '/users.png', text: 'Участие всех пользователей' },
];

function pluralizeLabel(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function calcTimeLeft(targetMs: number) {
  const diff = targetMs - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1_000);
  return { days, hours, minutes, seconds, total: diff };
}

export const RoadMapPage: React.FC<RoadMapPageProps> = ({ stats, whiteListStatus, maxUserStepPrice = 0, showBack = true }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const [expandedStages, setExpandedStages] = useState<Record<number, boolean>>({});

  const whiteListStartDate = stats?.white_list_start_date ? new Date(stats.white_list_start_date) : null;
  const opendoorStartDate = stats?.opendoor_start_date ? new Date(stats.opendoor_start_date) : null;

  const now = new Date();
  const nowMs = now.getTime();

  // White List: pending < wlStartDate, active between wlStartDate and odStartDate, completed >= odStartDate
  const getWlStatus = (): StageStatus => {
    if (!whiteListStartDate) return 'pending';
    if (nowMs < whiteListStartDate.getTime()) return 'pending';
    if (opendoorStartDate && nowMs >= opendoorStartDate.getTime()) return 'completed';
    return 'active';
  };

  // Open Door: pending < odStartDate, active >= odStartDate
  const getOdStatus = (): StageStatus => {
    if (!opendoorStartDate) return 'pending';
    return nowMs >= opendoorStartDate.getTime() ? 'active' : 'pending';
  };

  const wlStatus = getWlStatus();
  const odStatus = getOdStatus();
  const grStatus = 'pending' as StageStatus;

  const timerTargetMs = odStatus === 'pending' && opendoorStartDate ? opendoorStartDate.getTime() : null;

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');
  const openDoorTime = timerTargetMs && odStatus === 'pending' ? calcTimeLeft(timerTargetMs) : null;

  const handleSpeedUp = async () => {
    try {
      const response = await apiClient.getNFTs();
      const nfts = response?.data || [];
      const mostExpensive = (nfts as { id: string | number; price?: number | null }[]).reduce(
        (best: { id: string; price: number } | null, n: { id: string | number; price?: number | null }) =>
          !best || (n.price != null && n.price > best.price) ? { id: String(n.id), price: n.price ?? 0 } : best,
        null as { id: string; price: number } | null,
      );
      if (mostExpensive) {
        navigate(`/nft/${mostExpensive.id}`);
      } else {
        navigate('/');
      }
    } catch {
      navigate('/');
    }
  };

  const toggleStage = (stage: number) =>
    setExpandedStages((prev) => ({ ...prev, [stage]: !prev[stage] }));

  const statusLabel = (s: StageStatus) =>
    s === 'active' ? 'Активен' : s === 'completed' ? 'Завершён' : 'Ожидается';

  return (
    <div className="roadmap-page">
      <PageHeader title="Роадмап" showBack={showBack} />
      {/* User status card */}
      {whiteListStatus?.in_white_list && user && (
        <div className="rm-user-card">
          <div className="rm-user-row">
            {user.avatar_url && (
              <img src={user.avatar_url} alt="" className="rm-user-avatar" />
            )}
            <div className="rm-user-info">
              <span className="rm-user-title">{user.first_name}, вы в White List!</span>
              <span className="rm-user-subtitle">Ваш СБП успешно привязан</span>
            </div>
          </div>
        </div>
      )}

      {/* Stage 1: White List */}
      <div className={`rm-card rm-card--${wlStatus}`}>
        <div
          className={`rm-card-header ${wlStatus === 'completed' ? 'rm-card-header--clickable' : ''}`}
          onClick={wlStatus === 'completed' ? () => toggleStage(1) : undefined}
        >
          <h2 className="rm-card-title">1 Этап: White List</h2>
          <div className="rm-card-header-right">
            <span className={`rm-badge rm-badge--${wlStatus}`}>{statusLabel(wlStatus)}</span>
            {wlStatus === 'completed' && (
              <span className={`rm-chevron ${expandedStages[1] ? 'rm-chevron--open' : ''}`} />
            )}
          </div>
        </div>

        {(wlStatus !== 'completed' || expandedStages[1]) && (
          <div className="rm-card-body">
            <p className="rm-card-desc">
              White List — это ранний доступ для первых 1000 участников, которые:
            </p>

            <div className="rm-benefits-block">
              <div className="rm-benefit-highlight">
                <span className="rm-benefit-highlight-text">Получат свой первый NFT</span>
                <span className="rm-benefit-highlight-badge">бесплатно!</span>
              </div>
              <div className="rm-benefits-list">
                <div className="rm-benefit-row">
                  <div className="rm-benefit-icon-wrap">
                    <img src="/file-text.png" alt="" className="rm-benefit-icon-img" />
                  </div>
                  <span className="rm-benefit-text">Попадут в участие на всех этапах ху#ков</span>
                </div>
                <div className="rm-benefit-row">
                  <div className="rm-benefit-icon-wrap">
                    <img src="/crown.png" alt="" className="rm-benefit-icon-img" />
                  </div>
                  <span className="rm-benefit-text">Смогут покупать NFT раньше всех остальных</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stage 2: Open Door */}
      <div className={`rm-card rm-card--${odStatus}`}>
        <div
          className={`rm-card-header ${odStatus === 'completed' ? 'rm-card-header--clickable' : ''}`}
          onClick={odStatus === 'completed' ? () => toggleStage(2) : undefined}
        >
          <h2 className="rm-card-title">2 Этап: Open Door</h2>
          <div className="rm-card-header-right">
            <span className={`rm-badge rm-badge--${odStatus}`}>{statusLabel(odStatus)}</span>
            {odStatus === 'completed' && (
              <span className={`rm-chevron ${expandedStages[2] ? 'rm-chevron--open' : ''}`} />
            )}
          </div>
        </div>

        {(odStatus !== 'completed' || expandedStages[2]) && (
          <div className="rm-card-body">
            <p className="rm-card-desc">
              Open Door —этап, когда каждый может купить любую понравившуюся ху#ню
            </p>

            {odStatus === 'pending' && openDoorTime && openDoorTime.total > 0 && (
              <div className="drops-timer-section">
                <h3 className="timer-title">Этап Open Door через:</h3>
                <div className="countdown-box">
                  <div className="countdown-row">
                    {openDoorTime.days > 0 && (
                      <div className="countdown-days-wrap">
                        <div className="time-block">
                          <span className="time-value">{openDoorTime.days}</span>
                          <span className="time-label">
                            {pluralizeLabel(openDoorTime.days, 'день', 'дня', 'дней')}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="time-block">
                      <span className="time-value">{pad(openDoorTime.hours)}</span>
                      <span className="time-label">
                        {pluralizeLabel(openDoorTime.hours, 'час', 'часа', 'часов')}
                      </span>
                    </div>
                    <span className="time-sep">:</span>
                    <div className="time-block">
                      <span className="time-value">{pad(openDoorTime.minutes)}</span>
                      <span className="time-label">
                        {pluralizeLabel(openDoorTime.minutes, 'минута', 'минуты', 'минут')}
                      </span>
                    </div>
                    <span className="time-sep">:</span>
                    <div className="time-block">
                      <span className="time-value">{pad(openDoorTime.seconds)}</span>
                      <span className="time-label">
                        {pluralizeLabel(openDoorTime.seconds, 'секунда', 'секунды', 'секунд')}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="timer-note">После окончания White List откроется свободная покупка NFT</p>
              </div>
            )}

            <div className="rm-features-list">
              {OPEN_DOOR_FEATURES.map((f, i) => (
                <div className="rm-feature-row" key={i}>
                  <div className="rm-icon-wrap">
                    <img src={f.icon} alt="" className="rm-feature-icon" />
                  </div>
                  <span className="rm-feature-text">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stage 3: Гранитный */}
      <div className={`rm-card rm-card--${grStatus}`}>
        <div
          className={`rm-card-header ${grStatus === 'completed' ? 'rm-card-header--clickable' : ''}`}
          onClick={grStatus === 'completed' ? () => toggleStage(3) : undefined}
        >
          <h2 className="rm-card-title">Этап 3: Гранитный</h2>
          <div className="rm-card-header-right">
            <span className={`rm-badge rm-badge--${grStatus}`}>{statusLabel(grStatus)}</span>
            {grStatus === 'completed' && (
              <span className={`rm-chevron ${expandedStages[3] ? 'rm-chevron--open' : ''}`} />
            )}
          </div>
        </div>

        {(grStatus !== 'completed' || expandedStages[3]) && (
          <div className="rm-card-body">
            <p className="rm-card-desc">
              Гранитный —финальный этап эвента, когда NFT достигает 500 тыс. руб.
            </p>

            <div className="rm-progress-section">
              <div className="rm-progress-header">
                <span className="rm-progress-label">Шкала достижения этапа</span>
                <span className="rm-progress-value">
                  {Math.min(100, Math.round((maxUserStepPrice / GRANITE_TARGET_PRICE) * 100))} <span className="rm-progress-total">/ 100%</span>
                </span>
              </div>
              <div className="rm-progress-bar">
                <div
                  className="rm-progress-fill"
                  style={{ width: `${Math.min(100, (maxUserStepPrice / GRANITE_TARGET_PRICE) * 100)}%` }}
                />
              </div>
            </div>

            <button className="rm-speed-btn button button--primary button--primary-s button--full" onClick={handleSpeedUp}>
              Ускорить
            </button>

            <div className="rm-granite-features">
              {GRANITE_FEATURES.map((f, i) => (
                <div className="rm-granite-feature" key={i}>
                  <div className="rm-icon-wrap">
                    <img src={f.icon} alt="" className="rm-granite-icon" />
                  </div>
                  <div className="rm-granite-feature-text">
                    <span className="rm-granite-feature-title">{f.title}</span>
                    <span className="rm-granite-feature-desc">{f.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Standalone route page that fetches stats and renders RoadMapPage */
export const RoadmapRoutePage: React.FC = () => {
  const [stats, setStats] = useState<WhiteListStats | null>(null);
  const [wlStatus, setWlStatus] = useState<WhiteListStatus | null>(null);
  const [maxUserStepPrice, setMaxUserStepPrice] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, statusData, nftStatsData] = await Promise.all([
          apiClient.getWhiteListStats().catch(() => null),
          apiClient.getWhiteListStatus().catch(() => null),
          apiClient.getNftStats().catch(() => null),
        ]);
        setStats(statsData);
        setWlStatus(statusData);
        const maxPrice = nftStatsData?.max_step_price ?? 0;
        setMaxUserStepPrice(maxPrice);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="roadmap-page">
        <PageHeader title="Роадмап" />
        <div className="roadmap-loading">
          <div className="whitelist-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <RoadMapPage stats={stats} whiteListStatus={wlStatus} maxUserStepPrice={maxUserStepPrice} />
  );
};
